import { IElementData } from "@pageBuilder/typings";
import { CascadeDataCleanUpService } from "src/services/cascade-data-cleanup.service";
import { ElementStyleService } from "../services/element-style.service";
import { HtmlRenderMode, IRenderingCommonContext } from "@commonRendering/types";

export abstract class RendererBase<TData extends IElementData> {

    protected readonly styleService: ElementStyleService;

    constructor(protected readonly renderingCommonContext: IRenderingCommonContext) {
        this.styleService = new ElementStyleService();
    }

    public render(element: HTMLElement, data: TData): HTMLElement {
        if (data?.id) {
            element.id = data.id;
        }

        const renderingResult = this.renderInternal(element, data); // render first because some renderers change data when rendering (fixme)

        const cleanData = CascadeDataCleanUpService.cleanUp(data);
        element.setAttribute("data-element-json", JSON.stringify(cleanData));

        return renderingResult;
    }

    public rerender(element: HTMLElement, data: TData): HTMLElement {
        return this.rerenderInternal(element, data);
    }

    public getData(element: HTMLElement): TData | null {
        const attributeValue = element.getAttribute("data-element-json");
        if (!attributeValue) {
            return null;
        }

        const parsedData = JSON.parse(attributeValue) as TData;
        return CascadeDataCleanUpService.cleanUp(parsedData);
    }

    public get renderedDocument(): Document {
        return this.renderingCommonContext.renderedDoc;
    }

    public get isEditorRendering(): boolean {
        return this.getRenderingMode().toLocaleLowerCase() === "editor";
    }

    public get isPreviewRendering(): boolean {
        return this.getRenderingMode().toLocaleLowerCase() === "preview";
    }

    public get isPublishRendering(): boolean {
        return this.getRenderingMode().toLocaleLowerCase() === "published";
    }

    public get isPrerendering(): boolean {
        return this.getRenderingMode().toLocaleLowerCase() === "prerendering";
    }

    public getRenderingMode(): HtmlRenderMode {
        return this.renderingCommonContext.data.mode;
    }

    public createNew(): HTMLElement {
        return this.createNewInternal();
    }

    public cleanup(element: HTMLElement): void {
        this.cleanupAllElementStyles(element);
        this.cleanupInternal(element);
        element.remove();
    }

    protected cleanupAllElementStyles(element: HTMLElement): void {
        const selectors = this.getContentSelectors(element);
        if (selectors && selectors.length > 0) {
            selectors.forEach(selector => this.styleService.clearStyle(element, selector));
        }
    }

    public getContentSelectors(element: HTMLElement): Array<string> {
        return this.getContentSelectorsInternal(element);
    }

    protected rerenderInternal(element: HTMLElement, data: TData): HTMLElement {
        return this.renderInternal(element, data);
    }

    protected abstract createNewInternal(): HTMLElement;

    protected abstract renderInternal(element: HTMLElement, data: TData): HTMLElement;

    protected abstract cleanupInternal(element: HTMLElement): void;

    protected abstract getContentSelectorsInternal(element: HTMLElement): Array<string>;

}
